import { findProvinceCode } from "@redotech/locale/provinces";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import type { SelectedShippingRate } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import {
  FulfillmentGroupData,
  getSelectedRate,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { applyParcelWeightDistribution } from "@redotech/redo-fulfillment-operations-common/parcel-weight-distribution";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  DEFAULT_REDO_PARCEL,
  OutboundParcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import type { OutboundRate } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  getBillingAccountId,
  isExternalAccountBilling,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import {
  AddressAutocompleteFormValue,
  omsAddressAutocompleteForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { ParcelContext } from "../../services/parcel-service";
import { AddressForm } from "../address-form";
import { ParcelInput } from "../parcels/parcel-input";
import {
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchContext,
} from "../print-batch-modal";
import { ShipmentRateSelectionDropdown } from "../shipment-rate-selection-dropdown";
import type { OutboundRateOption } from "../util";

function getPrepopulatedForm(address: FulfillmentOrderAddress) {
  return {
    name: address.name || "",
    address1: address.address1 || "",
    address2: address.address2 || "",
    city: address.city || "",
    province: findProvinceCode(address.province || ""),
    zip: address.zip || "",
    countryCode: address.countryCode as RedoShopifyCountryCode,
    phone: address.phone || "",
    email: address.email || null,
    company: address.company || null,
  };
}

function addressFormToAddress(
  form: AddressAutocompleteFormValue,
): FulfillmentOrderAddress {
  return {
    name: form.name,
    address1: form.address1,
    address2: form.address2 || "",
    city: form.city,
    province: form.province || "",
    zip: form.zip,
    countryCode: form.countryCode,
    phone: form.phone || "",
    email: form.email || "",
    company: form.company || "",
  };
}

function returnRateToOption(rate: OutboundRate): OutboundRateOption {
  return {
    key: rate.id,
    carrier: rate.carrier,
    service: rate.service,
    rate: Number(rate.rate),
    currency: rate.currency,
    deliveryDays: rate.delivery_days?.toString(),
    transitDays: rate.transit_days?.toString(),
    redoRate: !rate.is_merchant_carrier_account,
    carrier_account_id: rate.carrier_account_id,
    saturdayDelivery: rate.saturday_delivery ?? undefined,
    provider: rate.provider,
    landedCost: rate.landedCost ?? undefined,
    isExternalBilling: isExternalAccountBilling(rate.payment_option),
    billingAccountId: getBillingAccountId(rate.payment_option),
  };
}

function rateOptionToSelectedShippingRate(
  option: OutboundRateOption,
): SelectedShippingRate {
  return {
    carrier: option.carrier,
    service: option.service,
    carrier_account_id: option.carrier_account_id,
  };
}

export const CreateReturnLabelModal = memo(function CreateReturnLabelModal({
  closeModal,
  fulfillmentGroup,
  isOpen,
}: {
  closeModal: () => void;
  fulfillmentGroup: FulfillmentGroupData;
  isOpen: boolean;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { printBatch } = useContext(PrintBatchContext);

  const [currentStep, setCurrentStep] = useState<
    "shopper" | "merchant" | "parcel" | "rate"
  >("shopper");

  const { parcels } = applyParcelWeightDistribution(
    fulfillmentGroup.selectedParcels,
    fulfillmentGroup.items,
  );

  const [selectedParcel, setSelectedParcel] = useState<OutboundParcel>(
    parcels[0] ?? DEFAULT_REDO_PARCEL,
  );
  const [returnRates, setReturnRates] = useState<OutboundRateOption[]>([]);
  const [selectedRate, setSelectedRate] = useState<OutboundRateOption | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const [parcelsLoad] = useLazyContext(ParcelContext);
  const parcelValues = parcelsLoad.value ?? [];

  const shopperInput = useInput(
    omsAddressAutocompleteForm,
    getPrepopulatedForm(fulfillmentGroup.destinationAddress),
  );
  const merchantInput = useInput(
    omsAddressAutocompleteForm,
    getPrepopulatedForm(
      fulfillmentGroup.overrideReturnAddress ?? fulfillmentGroup.originAddress,
    ),
  );

  const loadingRateOption = useMemo(() => {
    const selectedOutboundRate = getSelectedRate({
      selectedShippingRate: fulfillmentGroup.selectedShippingRate,
      availableRates: fulfillmentGroup.availableShipmentRates?.rates,
    });
    const rate =
      selectedOutboundRate ?? fulfillmentGroup.availableShipmentRates?.rates[0];

    return rate ? returnRateToOption(rate) : null;
  }, [
    fulfillmentGroup.availableShipmentRates?.rates,
    fulfillmentGroup.selectedShippingRate,
  ]);

  const handleRateShop = useHandler(async () => {
    setReturnRates([]);
    setSelectedRate(null);
    setCurrentStep("rate");
    setLoading(true);
    try {
      const result = await client.getOmsReturnLabelRates({
        fulfillmentGroupId: fulfillmentGroup._id,
        shopperAddress: addressFormToAddress(shopperInput.value),
        merchantAddress: addressFormToAddress(merchantInput.value),
        parcel: selectedParcel,
      });

      if (result.errorMessage) {
        toast(result.errorMessage, { variant: "error" });
        setCurrentStep("parcel");
        return;
      }

      const rateOptions = result.rates.map(returnRateToOption);
      if (rateOptions.length === 0) {
        toast("No return rates found for this shipment", { variant: "error" });
        setCurrentStep("parcel");
        return;
      }

      setReturnRates(rateOptions);
      setSelectedRate(rateOptions[0] ?? null);
    } catch (error) {
      toast("Failed to get return label rates", { variant: "error" });
      setCurrentStep("parcel");
    } finally {
      setLoading(false);
    }
  });

  const handleNext = useHandler(() => {
    switch (currentStep) {
      case "shopper": {
        setCurrentStep("merchant");

        break;
      }
      case "merchant": {
        setCurrentStep("parcel");

        break;
      }
      case "parcel": {
        void handleRateShop();

        break;
      }
      // No default
    }
  });

  const handleBack = useHandler(() => {
    switch (currentStep) {
      case "rate": {
        setCurrentStep("parcel");

        break;
      }
      case "parcel": {
        setCurrentStep("merchant");

        break;
      }
      case "merchant": {
        setCurrentStep("shopper");

        break;
      }
      // No default
    }
  });

  const isValid = useMemo(() => {
    switch (currentStep) {
      case "shopper": {
        return shopperInput.errors.length === 0;
      }
      case "merchant": {
        return merchantInput.errors.length === 0;
      }
      case "parcel": {
        return true;
      }
      case "rate": {
        return selectedRate !== null;
      }
      // No default
    }
    return true;
  }, [currentStep, shopperInput.errors, merchantInput.errors, selectedRate]);

  const handleClose = useHandler(() => {
    closeModal();
    setCurrentStep("shopper");
    setReturnRates([]);
    setSelectedRate(null);
  });

  const handleBuy = useHandler(async () => {
    if (!selectedRate) {
      return;
    }

    setLoading(true);
    try {
      const result = await client.buyReturnLabel({
        fulfillmentGroupId: fulfillmentGroup._id,
        shipmentRate: rateOptionToSelectedShippingRate(selectedRate),
      });

      if (!result.success) {
        toast(result.errorMessage ?? "Failed to create return label", {
          variant: "error",
        });
        return;
      }

      printBatch({
        document: PrintableDocument.RETURN_LABEL,
        batch: { fulfillmentGroupIds: [fulfillmentGroup._id] },
        documentPrintStatusMap: getDocumentPrintStatusMapForFulfillmentGroups([
          fulfillmentGroup,
        ]),
      });

      handleClose();
    } catch (error) {
      toast("Failed to create return label", { variant: "error" });
    } finally {
      setLoading(false);
    }
  });

  const handleSelectedRateChange = useHandler((rate: OutboundRateOption) => {
    setSelectedRate(rate);
  });

  const modalProps: RedoModalProps = {
    title: "Create return label",
    modalSize: "sm",
    isOpen,
    onModalCloseRequested: handleClose,
    primaryButton: {
      text:
        currentStep === "parcel"
          ? "Rate shop"
          : currentStep === "rate"
            ? "Buy & Print"
            : "Next",
      onClick: currentStep === "rate" ? handleBuy : handleNext,
      disabled: loading || !isValid,
    },
    secondaryButton: {
      text: currentStep === "shopper" ? "Cancel" : "Back",
      onClick: currentStep === "shopper" ? handleClose : handleBack,
    },
  };

  return (
    <RedoModal {...modalProps}>
      <Flex dir="column" gap="xl">
        {currentStep === "shopper" && (
          <AddressForm
            form={shopperInput}
            hideEmailField
            title="Shopper Address (Return From)"
          />
        )}

        {currentStep === "merchant" && (
          <AddressForm
            form={merchantInput}
            hideEmailField
            requirePhone={false}
            title="Merchant Address (Return To)"
          />
        )}

        {currentStep === "parcel" && selectedParcel && (
          <ParcelInput
            disableAutoReadFromScale
            displayedButtonAction={FulfillmentGroupActions.CREATE_RETURN_LABEL}
            everythingDisabled={loading}
            mixedParcels={false}
            mixedWeight={false}
            onParcelChange={(updatedParcel) => {
              setSelectedParcel({
                ...selectedParcel,
                ...(updatedParcel as OutboundParcel),
              });
              setReturnRates([]);
              setSelectedRate(null);
            }}
            parcelValues={parcelValues}
            selectedParcel={selectedParcel}
            selectedStatus={null}
            usedPackageLearning={false}
            usedWeightLearning={false}
          />
        )}

        {currentStep === "rate" && (
          <ShipmentRateSelectionDropdown
            disabled={loading}
            fullWidth
            label="Return service"
            loading={loading}
            mixedRates={false}
            options={returnRates}
            selectedRate={
              loading ? (selectedRate ?? loadingRateOption) : selectedRate
            }
            setSelectedRate={handleSelectedRateChange}
          />
        )}
      </Flex>
    </RedoModal>
  );
});
